import { useEffect, useLayoutEffect, useState, useRef, useCallback } from "react";
import { Modal, Button } from "antd";
import { EnvironmentFilled, WarningFilled } from "@ant-design/icons";
import { fetchApproximateLocationByIp, toGeolocationCoordinatesFromIp } from "../utils/ipGeolocation";
import "./LocationAccuracyModal.css";

interface LocationAccuracyModalProps {
  visible: boolean;
  onAccept: (coords: GeolocationCoordinates) => void;
  onAutoAccept: (coords: GeolocationCoordinates) => void;
  onCancel?: () => void;
  /** Když GPS nelze: zkusit přibližně podle IP (u zaměřování stánku vypnout) */
  allowIpFallback?: boolean;
}

const getBarsColorByAccuracy = (accuracy: number): string => {
  if (accuracy <= 12) return "#2f9e44";
  if (accuracy <= 20) return "#d48806";
  return "#cf1322";
};

export default function LocationAccuracyModal({
  visible,
  onAccept,
  onAutoAccept,
  onCancel,
  allowIpFallback = true,
}: LocationAccuracyModalProps) {
  const [coords, setCoords] = useState<GeolocationCoordinates | null>(null);
  const [geolocationAccessDenied, setGeolocationAccessDenied] = useState(false);
  const [requestingPosition, setRequestingPosition] = useState(false);
  const [noGeolocationApi, setNoGeolocationApi] = useState(false);
  const [insecureContext, setInsecureContext] = useState(false);
  /** Při zobrazení obrazovky „přístup odepřen“: reálný stav z Permissions API (prohlížeč po 'denied' znovu neptá) */
  const [geoPermissionDetail, setGeoPermissionDetail] = useState<PermissionState | "unknown" | null>(null);
  const geoPermRef = useRef<PermissionStatus | null>(null);
  const geoPermListenerRef = useRef<(() => void) | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const autoAcceptOnceRef = useRef(false);
  const autoAcceptTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [ipLookupFailed, setIpLookupFailed] = useState(false);
  const [ipButtonLoading, setIpButtonLoading] = useState(false);

  const safeAutoAccept = useCallback(
    (c: GeolocationCoordinates) => {
      if (autoAcceptOnceRef.current) return;
      autoAcceptOnceRef.current = true;
      onAutoAccept(c);
    },
    [onAutoAccept]
  );

  const safeAccept = useCallback(
    (c: GeolocationCoordinates) => {
      if (autoAcceptTimerRef.current) {
        clearTimeout(autoAcceptTimerRef.current);
        autoAcceptTimerRef.current = null;
      }
      autoAcceptOnceRef.current = true;
      onAccept(c);
    },
    [onAccept]
  );

  const handleUseIpLocation = useCallback(async () => {
    setIpButtonLoading(true);
    setIpLookupFailed(false);
    try {
      const loc = await fetchApproximateLocationByIp();
      if (loc) {
        safeAutoAccept(toGeolocationCoordinatesFromIp(loc));
      } else {
        setIpLookupFailed(true);
      }
    } catch {
      setIpLookupFailed(true);
      console.error("IP geolocation request failed");
    } finally {
      setIpButtonLoading(false);
    }
  }, [safeAutoAccept]);

  const startWatch = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (!navigator.geolocation) return;

    const id = navigator.geolocation.watchPosition(
      (pos) => {
        setGeolocationAccessDenied(false);
        setCoords((prev) => {
          const c = pos.coords;
          if (
            !prev ||
            prev.latitude !== c.latitude ||
            prev.longitude !== c.longitude ||
            prev.accuracy !== c.accuracy
          ) {
            return c;
          }
          return prev;
        });
      },
      (err) => {
        if (err.code === 1) {
          setGeolocationAccessDenied(true);
          setGeoPermissionDetail("unknown");
        }
      },
      { enableHighAccuracy: true, timeout: 2000, maximumAge: 0 }
    );
    watchIdRef.current = id;
  }, []);

  useLayoutEffect(() => {
    if (!visible) {
      return;
    }
    setInsecureContext(typeof window !== "undefined" && !window.isSecureContext);
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    if (insecureContext) {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      return;
    }
    if (geolocationAccessDenied) {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      return;
    }
    if (!navigator.geolocation) {
      setNoGeolocationApi(true);
      return;
    }
    setNoGeolocationApi(false);

    (async () => {
      try {
        if (navigator.permissions?.query) {
          const st = await navigator.permissions.query({ name: "geolocation" as PermissionName });
          if (st.state === "denied") {
            setGeolocationAccessDenied(true);
            setGeoPermissionDetail("denied");
            return;
          }
        }
      } catch {
        // Permissions API může chybět nebo geolocation v query nepodporovat
      }
      startWatch();
    })();

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [visible, insecureContext, geolocationAccessDenied, startWatch]);

  useEffect(() => {
    const removeGeoPermListener = () => {
      const p = geoPermRef.current;
      const fn = geoPermListenerRef.current;
      if (p && fn) {
        p.removeEventListener("change", fn);
      }
      geoPermRef.current = null;
      geoPermListenerRef.current = null;
    };

    if (!visible || !geolocationAccessDenied) {
      removeGeoPermListener();
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        if (navigator.permissions?.query) {
          const p = await navigator.permissions.query({ name: "geolocation" as PermissionName });
          if (cancelled) return;
          removeGeoPermListener();
          const onChange = () => {
            setGeoPermissionDetail(p.state);
            if (p.state === "granted") {
              setGeolocationAccessDenied(false);
              setGeoPermissionDetail(null);
            }
          };
          geoPermRef.current = p;
          geoPermListenerRef.current = onChange;
          setGeoPermissionDetail(p.state);
          if (p.state === "granted") {
            setGeolocationAccessDenied(false);
            setGeoPermissionDetail(null);
            removeGeoPermListener();
            return;
          }
          p.addEventListener("change", onChange);
        } else {
          if (!cancelled) setGeoPermissionDetail("unknown");
        }
      } catch {
        if (!cancelled) setGeoPermissionDetail("unknown");
      }
    })();

    return () => {
      cancelled = true;
      removeGeoPermListener();
    };
  }, [visible, geolocationAccessDenied]);

  const requestGeolocationPermission = useCallback(() => {
    if (typeof window !== "undefined" && !window.isSecureContext) {
      return;
    }
    if (!navigator.geolocation) {
      return;
    }
    setRequestingPosition(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeolocationAccessDenied(false);
        setCoords(pos.coords);
        setRequestingPosition(false);
      },
      (err) => {
        setRequestingPosition(false);
        if (err.code === 1) {
          setGeolocationAccessDenied(true);
        }
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  }, []);

  useEffect(() => {
    if (!visible) return;
    if (geolocationAccessDenied) return;
    if (!coords?.accuracy || coords.accuracy > 10) return;
    if (autoAcceptOnceRef.current) return;

    if (autoAcceptTimerRef.current) clearTimeout(autoAcceptTimerRef.current);
    autoAcceptTimerRef.current = setTimeout(() => {
      safeAutoAccept(coords);
      autoAcceptTimerRef.current = null;
    }, 600);
  }, [coords, geolocationAccessDenied, safeAutoAccept, visible]);

  useEffect(() => {
    if (!visible) {
      autoAcceptOnceRef.current = false;
      if (autoAcceptTimerRef.current) {
        clearTimeout(autoAcceptTimerRef.current);
        autoAcceptTimerRef.current = null;
      }
      setCoords(null);
      setGeolocationAccessDenied(false);
      setRequestingPosition(false);
      setNoGeolocationApi(false);
      setInsecureContext(false);
      setGeoPermissionDetail(null);
      setIpLookupFailed(false);
      setIpButtonLoading(false);
    }
  }, [visible]);

  const accuracy = coords?.accuracy ?? null;
  const barsColor = accuracy ? getBarsColorByAccuracy(accuracy) : "#8c8c8c";
  const accuracyText = accuracy !== null ? `±${Math.round(accuracy)} m` : "Hledám...";
  const accuracyLevelClass =
    accuracy == null ? "is-neutral" : accuracy <= 12 ? "is-good" : accuracy <= 20 ? "is-warn" : "is-bad";

  if (insecureContext) {
    if (!allowIpFallback) {
      return (
        <Modal open={visible} closable={false} footer={null} centered>
          <div className="lam-root">
            <div className="lam-denied-icon-wrap" aria-hidden>
              <WarningFilled className="lam-denied-icon" />
            </div>
            <p className="lam-denied-text">
              Pro zjišťování polohy je potřeba zabezpečené připojení (HTTPS).
            </p>
            <div className={`lam-actions ${onCancel ? "lam-actions-between" : "lam-actions-center"}`}>
              {onCancel && (
                <Button type="primary" danger size="large" onClick={() => onCancel()}>
                  Zrušit
                </Button>
              )}
            </div>
          </div>
        </Modal>
      );
    }
    return (
      <Modal open={visible} closable={false} footer={null} centered>
        <div className="lam-root">
          <div className="lam-denied-icon-wrap" aria-hidden>
            <WarningFilled className="lam-denied-icon" />
          </div>
          <p className="lam-denied-text">
            GPS v prohlížeči funguje jen na <strong>HTTPS</strong>. Můžete místo toho zkusit <strong> přibližnou polohu
            podle IP </strong> (řádově města / region, ne metry).
          </p>
          {ipLookupFailed ? (
            <p className="lam-denied-text" style={{ marginTop: 8 }}>
              Polohu z IP se nepodařilo zjistit. Zkuste znovu nebo použijte stránku přes HTTPS a povolte GPS.
            </p>
          ) : null}
          <div className="lam-actions-denied" style={{ marginTop: 16 }}>
            <Button
              type="primary"
              size="large"
              block
              loading={ipButtonLoading}
              onClick={handleUseIpLocation}
            >
              Použít polohu podle IP
            </Button>
            {onCancel && (
              <Button type="default" size="large" block onClick={() => onCancel()}>
                Zrušit
              </Button>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  if (noGeolocationApi) {
    if (!allowIpFallback) {
      return (
        <Modal open={visible} closable={false} footer={null} centered>
          <div className="lam-root">
            <div className="lam-denied-icon-wrap" aria-hidden>
              <WarningFilled className="lam-denied-icon" />
            </div>
            <p className="lam-denied-text">
              V tomto prohlížeči není k dispozici zjišťování polohy (geolokace).
            </p>
            <div className={`lam-actions ${onCancel ? "lam-actions-between" : "lam-actions-center"}`}>
              {onCancel && (
                <Button type="primary" danger size="large" onClick={() => onCancel()}>
                  Zrušit
                </Button>
              )}
            </div>
          </div>
        </Modal>
      );
    }
    return (
      <Modal open={visible} closable={false} footer={null} centered>
        <div className="lam-root">
          <div className="lam-denied-icon-wrap" aria-hidden>
            <WarningFilled className="lam-denied-icon" />
          </div>
          <p className="lam-denied-text">
            V prohlížeči není k dispozici geolokace. Můžete použít <strong>přibližnou polohu podle IP</strong> (město /
            oblast, ne přesné souřadnice stánku).
          </p>
          {ipLookupFailed ? (
            <p className="lam-denied-text" style={{ marginTop: 8 }}>
              Polohu z IP se nepodařilo zjistit. Zkuste to znovu.
            </p>
          ) : null}
          <div className="lam-actions-denied" style={{ marginTop: 16 }}>
            <Button type="primary" size="large" block loading={ipButtonLoading} onClick={handleUseIpLocation}>
              Použít polohu podle IP
            </Button>
            {onCancel && (
              <Button type="default" size="large" block onClick={() => onCancel()}>
                Zrušit
              </Button>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  if (geolocationAccessDenied) {
    const isBrowserDenied = geoPermissionDetail === "denied";
    return (
      <Modal open={visible} closable={false} footer={null} centered>
        <div className="lam-root">
          <div className="lam-denied-icon-wrap" aria-hidden>
            <EnvironmentFilled className="lam-denied-pin" />
          </div>
          {geoPermissionDetail === null ? (
            <p className="lam-denied-text">Kontroluji stav oprávnění…</p>
          ) : isBrowserDenied ? (
            <>
              {allowIpFallback && ipLookupFailed ? (
                <p className="lam-denied-text" style={{ marginBottom: 12 }}>
                  Přibližnou polohu z IP se nepodařilo zjistit. Můžete zkusit povolit GPS v prohlížeči.
                </p>
              ) : null}
              <p className="lam-denied-text lam-denied-lead">
                Prohlížeč u této stránky <strong>už znovu neukáže systémový dotaz</strong> na polohu. Pokud jste
                dříve vybrali „Odepřít“, polohu musíte zapnout v <strong>nastavení pro tuto stránku</strong>:
              </p>
              <ol className="lam-settings-steps">
                <li>
                  Klikněte na <strong>ikonu zámku</strong> nebo <strong>informací (i)</strong> vlevo od adresy v řádku.
                </li>
                <li>
                  Otevřete <strong>oprávnění pro tuto stránku</strong> (např. „Nastavení stránky“ / Site settings) a najděte
                  položku <strong>Poloha</strong> (Location).
                </li>
                <li>
                  Nastavte <strong>Povolit</strong> (Allow). V některých prohlížečích můžete místo toho vymazat zákaz
                  a zkusit to znovu tady.
                </li>
                <li>
                  Vraťte se sem a stiskněte tlačítko <strong>Zkusit znovu</strong> níže.
                </li>
              </ol>
              <p className="lam-denied-hint">Na telefonu může být nastavení u adresního řádku, v menu prohlížeče nebo v systémovém přehledu využití místa.</p>
            </>
          ) : (
            <>
              {allowIpFallback && ipLookupFailed ? (
                <p className="lam-denied-text" style={{ marginBottom: 12 }}>
                  Přibližnou polohu z IP se nepodařilo zjistit. Můžete zkusit povolit GPS v prohlížeči.
                </p>
              ) : null}
              <p className="lam-denied-text">
                Abychom zjistili přesnost polohy, prohlížeč potřebuje povolení. Klepněte na tlačítko — zobrazí se dotaz na
                přístup k poloze. Pokud jste dřív přístup odepřeli a dotaz se už nezobrazuje, otevřete stejnou ikonku u
                adresy a polohu v nastavení stránky povolte, pak tlačítko zopakujte.
              </p>
            </>
          )}
          <div className="lam-actions-denied">
            {allowIpFallback && geoPermissionDetail !== null ? (
              <Button
                type="default"
                size="large"
                block
                loading={ipButtonLoading}
                disabled={requestingPosition}
                onClick={handleUseIpLocation}
              >
                Použít přibližnou polohu podle IP
              </Button>
            ) : null}
            <Button
              type="primary"
              size="large"
              block
              disabled={geoPermissionDetail === null || ipButtonLoading}
              loading={requestingPosition}
              onClick={requestGeolocationPermission}
            >
              {isBrowserDenied && geoPermissionDetail !== null ? "Zkusit znovu" : "Povolit zjišťování polohy"}
            </Button>
            {onCancel && (
              <Button type="default" size="large" block danger onClick={() => onCancel()}>
                Zrušit
              </Button>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={visible} closable={false} footer={null} centered>
      <div className="lam-root">
        <div className="lam-pulse-wrap">
          <span className="lam-pulse-ring lam-pulse-ring-1" />
          <span className="lam-pulse-ring lam-pulse-ring-2" />
          <div className="lam-pin-circle">
            <EnvironmentFilled className="lam-pin-icon" />
          </div>
        </div>

        <div className="lam-accuracy-section">
          <div className="lam-searching-text">Zjišťování přesné polohy...</div>
          <div className={`lam-accuracy-box ${accuracyLevelClass}`}>
            <div className="lam-accuracy-left">
              <div className="lam-accuracy-label">PŘESNOST</div>
              <div className="lam-accuracy-value">{accuracyText}</div>
            </div>
            <div className="lam-bars" style={{ color: barsColor }} aria-hidden>
              <span />
              <span />
              <span />
              <span />
            </div>
          </div>
        </div>

        <div className={`lam-actions ${onCancel ? "lam-actions-between" : "lam-actions-center"}`}>
          {onCancel && (
            <Button type="primary" danger size="large" onClick={() => onCancel()}>
              Zrušit
            </Button>
          )}
          {coords !== null && (
            <Button type="primary" size="large" style={{ width: onCancel ? 'auto' : '100%' }} onClick={() => safeAccept(coords)}>
              Použít aktuální polohu
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
